/**
 * Полоса «Нужно ваше действие» — напоминание о незакрытых обязательных шагах
 * старта. Рисуется в Layout над содержимым страницы, рядом с баннером передачи
 * дел, и живёт по тому же принципу: объясняет состояние и даёт один переход.
 *
 * Считать нечего: полоса показывается ровно тогда, когда сервер вернул фазу
 * `setup`. Своего условия «пора ли напомнить» здесь нет намеренно — иначе
 * появилось бы второе правило готовности, и полоса могла бы погаснуть на
 * экране, где стартовый центр всё ещё требует действий.
 *
 * Запрос делит ключ кэша со страницей `/start`, поэтому переход на неё не тянет
 * второй раз то же самое, и уходит он только у учёток с личным делом: у
 * остальных (админ, партнёр, подрядчик) центра нет, и запрос вернул бы 404. Тот
 * же гард стоит у соседа по ключу в `HomeRedirect` — без него полоса наполняла
 * бы общий кэш ровно для тех, кого он исключает.
 */

import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ArrowRight } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

import { startCenter } from '../api/client'
import { useAuth } from '../contexts/AuthContext'

export default function StartCenterActionBanner() {
    const { pathname } = useLocation()
    const { user } = useAuth()
    const onStartCenter = pathname === '/start' || pathname.startsWith('/start/')
    // Уволенному строкой выше уже показан режим передачи дел. Звать его
    // «готовиться к работе» — прямое противоречие: выплату по этим шагам ему
    // никто не начислит.
    const departing = user?.linked_employee_status === 'terminated'

    const { data } = useQuery({
        queryKey: ['start-center', 'me'],
        queryFn: () => startCenter.me(),
        enabled: Boolean(user?.employee_id) && !departing,
        retry: false,
        // Полоса висит в общем каркасе, то есть запрос повторялся бы на каждом
        // переходе. Пять минут свежести хватает: шаг закрывается действием
        // человека на самой странице, а она инвалидирует ключ сама.
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    })

    if (onStartCenter || departing || !data || data.phase !== 'setup') return null

    const left = Math.max(0, (data.required?.total || 0) - (data.required?.completed || 0))

    return (
        <div
            style={{
                padding: '10px 16px',
                background: 'var(--color-warning-bg)',
                borderBottom: '1px solid color-mix(in srgb, var(--color-warning) 40%, transparent)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
            }}
            className='text-rel-sm'
        >
            <AlertCircle size={16} style={{ color: 'var(--color-warning)' }} />
            <span>
                <b>Нужно ваше действие.</b> До готовности к работе осталось
                шагов: {left}. Без них не получится начислить выплату и выдать
                часть доступов.
            </span>
            <Link
                to="/start"
                className="btn btn--toolbar btn--sm flex items-center gap-4"
                style={{ marginLeft: 'auto', textDecoration: 'none' }}
            >
                Открыть стартовый центр <ArrowRight size={13} />
            </Link>
        </div>
    )
}
